import type { GuestTheme, GuestThemeColors } from '../interfaces';

/**
 * Guest-portal theming utilities.
 *
 * The admin edits a small, curated set of color tokens (`GuestThemeColors`) plus a
 * preset font. `resolveThemeVars` expands that curated set into the full collection of
 * `#guest-portal` CSS custom properties that the portal's stylesheets consume. The same
 * function drives both the runtime `GuestThemeProvider` and the editor's live preview.
 *
 * Default-safety: `DEFAULT_GUEST_THEME` mirrors the values currently hardcoded in the
 * `#guest-portal` block of `src/index.css`, so applying the default theme reproduces the
 * portal's existing look (aside from derived button hover/active shades, see below).
 */

export interface FontOption {
  /** Stored value (matches the server `fontFamily` enum). */
  value: string;
  /** Human-readable label for the dropdown. */
  label: string;
  /** The CSS font-family stack applied via `--guest-font-family`. */
  stack: string;
}

export const FONT_OPTIONS: FontOption[] = [
  { value: 'Inter', label: 'Inter', stack: '"Inter", sans-serif' },
  {
    value: 'HelveticaNeueCyr',
    label: 'Helvetica Neue Cyr',
    stack: '"HelveticaNeueCyr", "Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  {
    value: 'System UI',
    label: 'System UI',
    stack: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  { value: 'Roboto', label: 'Roboto', stack: '"Roboto", sans-serif' },
  { value: 'Lato', label: 'Lato', stack: '"Lato", sans-serif' },
];

const DEFAULT_FONT_STACK = FONT_OPTIONS[0].stack;

/** Resolve a stored font value to its CSS stack, falling back to the default. */
export const getFontStack = (fontFamily: string): string =>
  FONT_OPTIONS.find((option) => option.value === fontFamily)?.stack ?? DEFAULT_FONT_STACK;

/**
 * Default guest-portal theme. Values match the current `#guest-portal` palette in
 * `src/index.css` so the default reproduces today's appearance.
 */
export const DEFAULT_GUEST_THEME: GuestTheme = {
  colors: {
    primary: '#2ce983',
    bodyBackground: '#0f0f0f',
    containerBackground: '#232323',
    modalBackground: '#0f0f0f',
    primaryText: '#ffffff',
    secondaryText: '#afafaf',
    inputBackground: '#0f0f0f',
    border: '#515151',
    // The access-code card keeps its original white "keycard" look by default.
    accessCodeBackground: '#ffffff',
    accessCodeText: '#000000',
    // Back button keeps its original dark circle + white arrow by default.
    backButtonBackground: '#202020',
    backButtonIcon: '#ffffff',
    // Rating stars keep classic gold fill + grey outline by default.
    ratingStarActive: '#ffce00',
    ratingStarInactive: '#808080',
    // Signature box keeps its white surface + black ink by default.
    signatureBackground: '#ffffff',
    signatureInk: '#000000',
    // Alert colors — error red + warning amber by default.
    error: '#ff7c7c',
    warning: '#ffce00',
  },
  // Mirrors the current `#guest-portal .container` font so the default theme
  // reproduces today's look. Inter and others remain selectable in FONT_OPTIONS.
  fontFamily: 'HelveticaNeueCyr',
};

// --- Color helpers -----------------------------------------------------------

const clamp = (value: number): number => Math.max(0, Math.min(255, Math.round(value)));

/** Expand `#rgb`/`#rgba` shorthand and drop any alpha channel -> `#rrggbb`. */
const normalizeHex = (hex: string): string => {
  let value = hex.trim().replace('#', '');
  if (value.length === 3 || value.length === 4) {
    value = value
      .slice(0, 3)
      .split('')
      .map((char) => char + char)
      .join('');
  }
  if (value.length === 8) {
    value = value.slice(0, 6);
  }
  return `#${value}`;
};

type Rgb = [number, number, number];

const hexToRgb = (hex: string): Rgb => {
  const value = normalizeHex(hex).replace('#', '');
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
};

const rgbToHex = ([r, g, b]: Rgb): string =>
  `#${[r, g, b].map((c) => clamp(c).toString(16).padStart(2, '0')).join('')}`;

/** Comma-separated channels for use inside `rgba(var(--x), a)`. */
export const hexToRgbChannels = (hex: string): string => hexToRgb(hex).join(', ');

/** Mix `hex` toward `target` by `amount` (0..1). */
const mix = (hex: string, target: Rgb, amount: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex([
    r + (target[0] - r) * amount,
    g + (target[1] - g) * amount,
    b + (target[2] - b) * amount,
  ]);
};

const lighten = (hex: string, amount: number): string =>
  mix(hex, [255, 255, 255], amount);
const darken = (hex: string, amount: number): string => mix(hex, [0, 0, 0], amount);

// --- Token expansion ---------------------------------------------------------

/**
 * Expand the curated theme into the full map of `#guest-portal` CSS custom properties.
 * Keys include the leading `--`. Button hover/active and a few secondary shades are
 * derived (lighten/darken) from their base token rather than stored, to keep the editor
 * non-technical.
 */
export const resolveThemeVars = (theme: GuestTheme): Record<string, string> => {
  const c: GuestThemeColors = theme.colors;

  return {
    // Backgrounds (body)
    '--main-background-color': c.bodyBackground,
    '--main-background-rgb': hexToRgbChannels(c.bodyBackground),
    '--table-header-background': c.bodyBackground,
    '--modal-footer-bg-color': c.bodyBackground,
    '--top-search-input-color': c.bodyBackground,

    // Backgrounds (containers / surfaces)
    '--main-container-bg-color': c.containerBackground,
    '--secondary-button-color': c.containerBackground,
    '--secondary-button-hover-color': lighten(c.containerBackground, 0.08),
    '--secondary-button-active-color': darken(c.containerBackground, 0.4),
    '--primary-button-border-color': c.containerBackground,
    '--top-menu-background': c.containerBackground,
    '--row-table-bg-color': c.containerBackground,
    '--article-card-bg-color': c.containerBackground,
    '--table-row-hover': lighten(c.containerBackground, 0.12),

    // Modal
    '--modal-background-color': c.modalBackground,

    // Brand / accent
    '--primary-button-color': c.primary,
    '--primary-button-hover-color': lighten(c.primary, 0.22),
    '--primary-button-active-color': darken(c.primary, 0.18),
    '--logo-icon-color': c.primary,
    '--pagination-active-color': c.primary,
    '--rmsc-main': c.primary,

    // Text
    '--primary-font-color': c.primaryText,
    '--primary-button-font-color': c.primaryText,
    '--secondary-font-color': c.secondaryText,
    '--secondary-button-font-color': c.secondaryText,

    // Inputs (border kept tied to the input background so it stays flush, as today)
    '--text-input-bg-color': c.inputBackground,
    '--input-background-color': c.inputBackground,
    '--text-input-border-color': c.inputBackground,

    // Visible control / divider borders
    '--guest-border-color': c.border,

    // Access-code / door-code card (its own surface). The internal dividers derive from
    // a midpoint between its text and background so they always contrast with the card.
    '--access-code-bg-color': c.accessCodeBackground,
    '--access-code-text-color': c.accessCodeText,
    '--access-code-border-color': mix(
      c.accessCodeText,
      hexToRgb(c.accessCodeBackground),
      0.6,
    ),

    // Back button (its own surface + icon)
    '--back-button-bg-color': c.backButtonBackground,
    '--back-button-icon-color': c.backButtonIcon,

    // Rating stars (filled + outline)
    '--rating-star-active-color': c.ratingStarActive,
    '--rating-star-inactive-color': c.ratingStarInactive,

    // Signature box (surface + ink)
    '--signature-bg-color': c.signatureBackground,
    '--signature-ink-color': c.signatureInk,

    // Alert colors
    '--error-color': c.error,
    '--warning-color': c.warning,

    // Typography
    '--guest-font-family': getFontStack(theme.fontFamily),
  };
};

/** Build a CSS rule string scoping the resolved vars to a selector. */
export const buildThemeCss = (theme: GuestTheme, selector = '#guest-portal'): string => {
  const body = Object.entries(resolveThemeVars(theme))
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
  return `${selector} {\n${body}\n}`;
};
